/**
 * Like or unlike a comment.
 *
 * Mirrors Flutter ContentReactionsRepository.likeComment:
 *   1. Optimistically update SecureStorage (for instant UI feedback)
 *   2. Call backend to persist the like + notification
 *   3. Revert SecureStorage if the backend call fails
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { CommentLikeRemapper } from '@/domain/remappers/commentLike/commentLikeRemapper';
import { CommentService } from '@/domain/services/comment/commentService';
import { COMMENTS_CONSTANTS } from '@/domain/constants/commentsConstants';
import type { CommentLikeDto } from '@/data/dtos/commentLike/commentLikeDto';
import type { CommentLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';

export interface LikeCommentParams {
  like: CommentLikeDto;
  notification: NotificationInfoDto;
  popularity?: number;
}

export class LikeCommentUseCase {
  constructor(
    private service = CommentService.getInstance(),
    private storage = SecureStorageWebCrypto.getInstance(),
  ) {}

  async execute(params: LikeCommentParams): Promise<boolean> {
    const popularity =
      params.popularity ?? COMMENTS_CONSTANTS.DEFAULT_LIKE_POPULARITY_FOR_COMMENT;

    const storageKey = SECURE_STORAGE_KEYS.COMMENT_LIKES + params.like.commentId;
    const previousLike = await this.storage.getEncrypted<CommentLikeStorage>(storageKey);

    const newLike: CommentLikeStorage = CommentLikeRemapper.toStorage({
      ...params.like,
      createdDate: previousLike?.createdDate ?? params.like.createdDate,
      updatedDate: Date.now(),
    });
    await this.storage.setEncrypted(storageKey, newLike);

    const success = await this.service.likeComment(
      params.like,
      params.notification,
      popularity,
    );

    if (!success) {
      if (previousLike) {
        await this.storage.setEncrypted(storageKey, previousLike);
      } else {
        await this.storage.remove(storageKey);
      }
    }

    return success;
  }
}

export const likeCommentUseCase = new LikeCommentUseCase();
